/** Dashboard widget: upcoming economic calendar events. */

import React, { useEffect, useState } from "react";
import { apiClient } from "@/api/client";
import type { EconomicEvent } from "@/types";
import WidgetCard from "./WidgetCard";

const IMPORTANCE_COLOR: Record<string, string> = {
  high: "text-signal-rose",
  medium: "text-signal-amber",
  low: "text-mist-400",
};

export default function EconomicCalendarWidget(): React.ReactElement {
  const [events, setEvents] = useState<EconomicEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get<EconomicEvent[]>("/macro/events/upcoming", { params: { within_days: 14 } })
      .then(({ data }) => setEvents(Array.isArray(data) ? data : []))
      .catch(() => setError("Unable to load the economic calendar"));
  }, []);

  return (
    <WidgetCard title="Economic calendar" isLoading={events === null && !error} error={error}>
      {events && events.length > 0 ? (
        <ul className="space-y-2">
          {events.slice(0, 5).map((event) => (
            <li key={event.id} className="flex items-center justify-between text-sm">
              <span className="text-mist-200">{event.event_name}</span>
              <span className={`text-xs ${IMPORTANCE_COLOR[event.importance]}`}>
                {new Date(event.event_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-mist-400">No upcoming events in the next 14 days.</p>
      )}
    </WidgetCard>
  );
}
